import { CheckmarkIcon } from "vega-icons";

import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { Checkbox } from "@vega-core/components/shadcn/checkbox";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";

import {
  type AccessAlreadyRow,
  type AccessGrantRow,
  type AccessNonUserRow,
} from "./draftEmailUtils";

type EmailAccessSectionProps = {
  grants: AccessGrantRow[];
  alreadyHasAccess: AccessAlreadyRow[];
  nonUsers: AccessNonUserRow[];
  roleOptions: OptionType[];
  disabled?: boolean;
  onToggleGrant: (userId: string, on: boolean) => void;
  onChangeGrantRole: (userId: string, roleName: string) => void;
};

export function EmailAccessSection({
  grants,
  alreadyHasAccess,
  nonUsers,
  roleOptions,
  disabled = false,
  onToggleGrant,
  onChangeGrantRole,
}: EmailAccessSectionProps) {
  const isEmpty =
    grants.length === 0 &&
    alreadyHasAccess.length === 0 &&
    nonUsers.length === 0;

  return (
    <div
      className="flex flex-col gap-1 border-t border-border/primary pt-4"
      data-testid="email-access-section"
    >
      <div className="flex items-baseline justify-between">
        <span className="body-base-medium text-text/primary">
          {m.coInvestments_draftEmail_access_title()}
        </span>
        <span className="body-small-regular text-text/secondary">
          {m.coInvestments_draftEmail_access_neverRemoves()}
        </span>
      </div>

      <div className="flex flex-col divide-y divide-border/subtle">
        {grants.map((grant) => (
          <div
            className="flex items-center gap-2.5 py-2"
            data-testid={`access-grant-${grant.userId}`}
            key={grant.userId}
          >
            <Checkbox
              checked={grant.on}
              data-testid={`access-grant-toggle-${grant.userId}`}
              disabled={disabled}
              onCheckedChange={(checked) =>
                onToggleGrant(grant.userId, checked === true)
              }
            />
            <div className="min-w-0 grow">
              <span className="body-small-medium text-text/primary">
                {grant.name}
              </span>
              {grant.inCc ? (
                <span className="body-small-regular text-text/secondary">
                  {" "}
                  {m.coInvestments_draftEmail_access_inCc()}
                </span>
              ) : null}
            </div>
            <ThemedDropdown
              className="w-max shrink-0"
              disabled={disabled || !grant.on}
              isClearable={false}
              name={`access-grant-role-${grant.userId}`}
              onChange={(option) => {
                if (option) onChangeGrantRole(grant.userId, option.value);
              }}
              options={roleOptions}
              value={grant.roleName}
            />
          </div>
        ))}

        {alreadyHasAccess.map((row) => (
          <div
            className="flex items-center gap-2.5 py-2 text-text/secondary"
            data-testid={`access-already-${row.userId}`}
            key={row.userId}
          >
            <CheckmarkIcon className="size-3.5 shrink-0 text-text/positive" />
            <div className="min-w-0 grow body-small-regular">{row.name}</div>
            <span className="body-small-regular shrink-0">
              {m.coInvestments_draftEmail_access_alreadyHasAccess({
                role: row.roleName,
              })}
            </span>
          </div>
        ))}

        {nonUsers.map((row) => (
          <div
            className="flex items-center gap-2.5 py-2 text-text/secondary"
            data-testid={`access-nonuser-${row.email}`}
            key={row.email}
          >
            <span className="w-3.5 shrink-0 text-center">—</span>
            <div className="min-w-0 grow body-small-regular">{row.email}</div>
            <span className="body-small-regular shrink-0">
              {m.coInvestments_draftEmail_access_emailOnly()}
            </span>
          </div>
        ))}

        {isEmpty ? (
          <p
            className="body-small-regular text-text/secondary py-2"
            data-testid="access-empty"
          >
            {m.coInvestments_draftEmail_access_empty()}
          </p>
        ) : null}
      </div>
    </div>
  );
}
